/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { call, put, SagaReturnType, takeLatest } from "redux-saga/effects";
import { formatError } from "utils/errorHelper";
import api from "_config/api";

export interface ObterDetalheSolicitacaoRequest {
  idSolicitacao: string;
}

// Arquivo anexado a uma resposta do formulário. `enunciado` = campo de origem.
export interface ArquivoDetalheSolicitacao {
  id: string;
  idResposta: string;
  nome: string;
  originalFileName?: string;
  enunciado?: string;
  ordem?: number;
  // Vínculo com pendência (contrato trazido do develop; preservado no merge).
  idPendenciaVinculada?: string | null;
  descricaoPendenciaVinculada?: string | null;
}

// Agrupamento dos arquivos por campo do formulário (enunciado), montado no
// backend (`arquivosAgrupados`) para a "Documentação anexada".
export interface GrupoArquivoDetalheSolicitacao {
  enunciado: string;
  ordem: number;
  arquivos: ArquivoDetalheSolicitacao[];
}

export interface DetalheSolicitacao {
  id: string;
  nomeRequerimento: string;
  /**
   * PBI 2576 — o backend já envia; serve para buscar a estrutura do tipo de
   * requerimento (opções de select, máscaras) na correção de campos.
   */
  idTipoRequerimento?: string;
  nomeUsuario: string;
  cpf: string;
  email: string;
  telefone?: string;
  protocolo: string;
  dataEnvioFormatada?: string;
  dataUltimaAtualizacaoFormatada?: string;
  status?: {
    value: number;
    name: string;
  };
  informacoesRequerimentos: {
    idSecao: string | null;
    nomeSecao: string;
    repetivel: boolean;
    // Host cidadão retorna tipoSecao como STRING (name); ausente => fallback genérico.
    tipoSecao?: string;
    repeticoes: {
      indice: number;
      respostas: {
        enunciado: string;
        resposta: string;
        tipo: string;
      }[];
    }[];
  }[];
  arquivos: ArquivoDetalheSolicitacao[];
  // Mesmos arquivos, agrupados pelo campo do formulário (enunciado).
  arquivosAgrupados?: GrupoArquivoDetalheSolicitacao[];
  etapas: {
    idEtapa: string;
    isConcluida: boolean;
    isAtual: boolean;
    nome: string;
    dataConclusaoFormatada?: string | null;
    responsavelConclusao?: string | null;
    ordem: number;
    equipe?: string;
    observacao?: string;
    situacaoEtapaSolicitacao?: {
      value: number;
      name: string;
    };
    arquivosConclusao: {
      id: string;
      idResposta: string;
      nome: string;
      originalFileName?: string;
      idSolicitacaoEtapa?: string;
    }[];
  }[];
  pendencias: {
    id: string;
    // Smart enum SituacaoPendencia serializado como { name, value }.
    situacao?: {
      name: string; // "Aguardando Informação" | "Informação Recebida" | "Resolvida"
      value: string; // "AGUARDANDO_INFORMACAO" | "INFORMACAO_RECEBIDA" | "RESOLVIDA"
    } | null;
    status?: string;
    dataFormatada?: string;
    comentario?: string;
    observacao?: string;
    responsavel?: string;
    destinatario?: string;
    respondidoPor?: string | null;
    dataResposta?: string | null;
    resolvidoPor?: string | null;
    dataResolucao?: string | null;
    respostas: {
      responsavel?: string;
      comentario?: string;
      isColaborador?: boolean;
      dataCriacao?: string;
      dataCriacaoFormatada?: string;
      arquivos?: {
        id: string;
        idResposta: string;
        nome?: string;
        originalFileName?: string;
      }[];
    }[];
    arquivos?: {
      id: string;
      idResposta: string;
      nome?: string;
      originalFileName?: string;
    }[];
    arquivosSolicitacaoVinculados?: {
      id: string;
      nome: string;
      nomeOriginal: string;
    }[];
    /**
     * PBI 2576 — campos do formulário que o analista liberou para correção.
     * Opcional: tolera backend sem a feature.
     *
     * ATENÇÃO: neste host `nomeSecao` vem SEMPRE null (o backend declara e não
     * preenche). O casamento com a estrutura do formulário tem de ser por
     * `idSecao` + `enunciado`, nunca por nome de seção.
     */
    camposSolicitacao?: {
      idRespostaSolicitacao: string;
      idSecao: string | null;
      nomeSecao: string | null;
      enunciado: string;
      tipoQuestao: string;
      indiceRepeticao: number;
      valorAtual: string | null;
      justificativa?: string | null;
    }[];
  }[];
}

interface State {
  isRequesting: boolean;
  successPayload: DetalheSolicitacao | null;
  errors: string[];
}

const initialState: State = {
  isRequesting: false,
  successPayload: null,
  errors: []
};

const obterDetalheSolicitacaoSlice = createSlice({
  name: "licenciamentourbano@obterDetalheSolicitacao",
  initialState,
  reducers: {
    obterDetalheSolicitacao: (
      state,
      action: PayloadAction<ObterDetalheSolicitacaoRequest>
    ): void => {
      state.isRequesting = true;
      state.errors = [];
    },
    obterDetalheSolicitacaoSuccess: (
      state,
      action: PayloadAction<DetalheSolicitacao>
    ): void => {
      state.isRequesting = false;
      state.successPayload = action.payload;
    },
    obterDetalheSolicitacaoFailed: (
      state,
      action: PayloadAction<string[]>
    ): void => {
      state.isRequesting = false;
      state.errors = action.payload;
    }
  }
});

function* sagaFunction(params: {
  type: string;
  payload: ObterDetalheSolicitacaoRequest;
}) {
  try {
    const response: SagaReturnType<typeof api> = yield call(
      api.get,
      `api/v1/cidadao/licenciamentourbano/detalhe/${params.payload.idSolicitacao}`
    );

    const { data } = response;

    yield put(obterDetalheSolicitacaoSuccess(data));
  } catch (error) {
    yield put(obterDetalheSolicitacaoFailed(formatError(error)));
  }
}

export const {
  obterDetalheSolicitacao,
  obterDetalheSolicitacaoSuccess,
  obterDetalheSolicitacaoFailed
} = obterDetalheSolicitacaoSlice.actions;

export const { reducer } = obterDetalheSolicitacaoSlice;

export const saga = takeLatest(obterDetalheSolicitacao.type, sagaFunction);
